import * as Select from '@radix-ui/react-select';
import { observer } from 'mobx-react-lite';
import type { MouseEvent, SyntheticEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ResumeChooserStore } from './ResumeChooser.store';
import './resume-selector.scss';
import {
   CheckIcon,
   DownloadIcon,
   ExpandMoreIcon,
   InsertDriveFileIcon,
   VisibilityIcon,
} from 'shared/icons';
import { Link } from 'react-router-dom';
import { Spinner } from '@radix-ui/themes';

type ResumeSelectorViewProps = {
   store: ResumeChooserStore;
   onUploadedFileAction?: (event: MouseEvent<HTMLButtonElement>, fileId: number) => void;
};

export function ResumeSelectorView({ store, onUploadedFileAction }: ResumeSelectorViewProps) {
   const { t } = useTranslation('global');
   const [open, setOpen] = useState(false);

   const preventActionSelect = (event: SyntheticEvent) => {
      event.preventDefault();
      event.stopPropagation();
   };

   return (
      <div className="resume-selector">
         <Select.Root
            open={open}
            onOpenChange={setOpen}
            value={store.selectedResumeValue}
            onValueChange={(value) => store.selectResumeByValue(value)}
            disabled={store.loading}
         >
            <Select.Trigger
               className="resume-selector__trigger"
               aria-label={t('jobDescription.selectResume')}
            >
               <span className="resume-selector__trigger-content">
                  <InsertDriveFileIcon
                     width={20}
                     height={20}
                     className="resume-selector__trigger-file-icon"
                     aria-hidden="true"
                  />
                  <Select.Value placeholder={t('jobDescription.selectResume')} />
               </span>

               
                <Spinner loading={store.loading }/>

               <Select.Icon className="resume-selector__trigger-chevron" aria-hidden="true">
                  <ExpandMoreIcon width={18} height={18} />
               </Select.Icon>
            </Select.Trigger>

            <Select.Portal>
               <Select.Content
                  className="resume-selector__content"
                  position="popper"
                  sideOffset={4}
               >
                  <Select.Viewport className="resume-selector__viewport">
                     {store.activeCvList.map((cv) => (
                        <div className="resume-selector__row" key={`active:${cv.id}`}>
                           <Select.Item
                              className="resume-selector__item"
                              value={`active:${cv.id}`}
                              textValue={cv.name}
                           >
                              <Select.ItemIndicator className="resume-selector__item-indicator">
                                 <CheckIcon width={14} height={14} />
                              </Select.ItemIndicator>
                              <Select.ItemText className="resume-selector__item-text">
                                 {cv.name}
                              </Select.ItemText>
                           </Select.Item>

                           <Link
                              target="_blank"
                              to={`/candidate/my-cv-list/${cv.id}`}
                              className="resume-selector__action-button"
                              title={t('jobDescription.view')}
                              onPointerDown={preventActionSelect}
                              onMouseDown={preventActionSelect}
                              onClick={(event) => event.stopPropagation()}
                           >
                              <VisibilityIcon width={18} height={18} />
                           </Link>
                        </div>
                     ))}

                     {store.cvList.map((cv) => (
                        <div className="resume-selector__row" key={`uploaded:${cv.id}`}>
                           <Select.Item
                              className="resume-selector__item"
                              value={`uploaded:${cv.id}`}
                              textValue={cv.file.name}
                           >
                              <Select.ItemIndicator className="resume-selector__item-indicator">
                                 <CheckIcon width={14} height={14} />
                              </Select.ItemIndicator>
                              <Select.ItemText className="resume-selector__item-text">
                                 {cv.file.name}
                              </Select.ItemText>
                           </Select.Item>
                           <button
                              type="button"
                              className="resume-selector__action-button"
                              title={t('jobDescription.view')}
                              onPointerDown={preventActionSelect}
                              onMouseDown={preventActionSelect}
                              onClick={(event) => {
                                 preventActionSelect(event);
                                 onUploadedFileAction?.(event, cv.file_id);
                              }}
                           >
                              <DownloadIcon width={18} height={18} />
                           </button>
                        </div>
                     ))}
                  </Select.Viewport>
               </Select.Content>
            </Select.Portal>
         </Select.Root>
      </div>
   );
}

export const ResumeSelector = observer(ResumeSelectorView);
